import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../components/Toast';

function TakeExam() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { notify, confirm } = useToast();

  const [test, setTest] = useState(null);
  const [examQuestions, setExamQuestions] = useState([]); // questions this candidate sees
  const [submissionId, setSubmissionId] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [dontKnow, setDontKnow] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [startedAt] = useState(new Date().toISOString());
  const [cheatFlags, setCheatFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [mediaStatus, setMediaStatus] = useState('idle'); // idle | requesting | granted | denied
  const [mediaError, setMediaError] = useState('');
  const [cameraLost, setCameraLost] = useState(false);

  const submittedRef = useRef(false);
  const cheatFlagsRef = useRef([]);
  const testRef = useRef(null);
  const answersRef = useRef([]);
  const currentQuestionRef = useRef(0);
  const submissionIdRef = useRef(null);
  const examQuestionsRef = useRef([]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const startedRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { cheatFlagsRef.current = cheatFlags; }, [cheatFlags]);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { currentQuestionRef.current = currentQuestion; }, [currentQuestion]);
  useEffect(() => { submissionIdRef.current = submissionId; }, [submissionId]);
  useEffect(() => { examQuestionsRef.current = examQuestions; }, [examQuestions]);

  // Proctoring: camera + mic are required to take the exam. Nothing is
  // recorded or uploaded — the live self-view is shown to the candidate
  // purely as a monitoring deterrent. Losing the stream mid-exam is warned
  // and flagged via the existing cheatFlags pipeline.
  const handleMediaStop = useCallback(() => {
    setCameraLost(true);
    const flag = `camera_or_mic_stopped_during_Q${currentQuestionRef.current + 1}_at_${new Date().toISOString()}`;
    setCheatFlags(prev => [...prev, flag]);
    notify('Your camera or microphone was turned off. This has been flagged. Re-enable it to continue.', 'warning');
  }, [notify]);

  const requestMedia = useCallback(async () => {
    setMediaError('');
    setMediaStatus(prev => (prev === 'granted' ? prev : 'requesting'));
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('unsupported');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      streamRef.current = stream;
      stream.getTracks().forEach(track => { track.onended = handleMediaStop; });
      setCameraLost(false);
      setMediaStatus('granted');
    } catch (err) {
      setMediaStatus('denied');
      setMediaError(
        err.message === 'unsupported'
          ? 'Your browser does not support camera access, or this page is not served over HTTPS.'
          : 'Camera and microphone access is required to take this exam. Please allow access in your browser, then retry.'
      );
    }
  }, [handleMediaStop]);

  // Request media on mount (only if authenticated; the fetch effect redirects otherwise)
  useEffect(() => {
    if (!sessionStorage.getItem('candidateToken')) return;
    requestMedia();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Attach the live stream to the on-screen tile once the exam renders
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [mediaStatus, loading, cameraLost]);

  // Submit exam
  const submitExam = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const candidateToken = sessionStorage.getItem('candidateToken');
    const currentTest = testRef.current;

    if (!candidateToken || !currentTest) {
      navigate(`/exam/${slug}`);
      return;
    }

    const currentAnswers = answersRef.current;
    const currentExamQuestions = examQuestionsRef.current;

    // Map answers back to original question indices
    const submissionAnswers = currentAnswers.map((answer, i) => ({
      questionIndex: currentExamQuestions[i]?.originalIndex ?? i,
      answer: answer || ''
    }));

    try {
      const res = await axios.post('/api/submissions', {
        testId: currentTest._id,
        submissionId: submissionIdRef.current,
        answers: submissionAnswers,
        startedAt,
        cheatFlags: cheatFlagsRef.current
      }, {
        headers: { Authorization: `Bearer ${candidateToken}` }
      });

      const submission = res.data.submission || res.data;
      navigate(`/exam/${slug}/complete`, {
        state: {
          submission,
          cheatFlags: cheatFlagsRef.current
        }
      });
    } catch (err) {
      if (err.response?.data?.alreadySubmitted) {
        // Submission was already saved (e.g. previous attempt succeeded despite timeout)
        navigate(`/exam/${slug}/complete`, {
          state: { submission: null, cheatFlags: cheatFlagsRef.current }
        });
        return;
      }
      setError('Failed to submit exam. Please try again.');
      submittedRef.current = false;
      setSubmitting(false);
    }
  }, [slug, startedAt, navigate]);

  // Fetch test and start exam
  useEffect(() => {
    const candidateToken = sessionStorage.getItem('candidateToken');
    if (!candidateToken) {
      navigate(`/exam/${slug}`);
      return;
    }
    // Block exam start until camera + mic are granted; run the load only once
    if (mediaStatus !== 'granted' || startedRef.current) return;
    startedRef.current = true;

    const fetchAndStart = async () => {
      try {
        // 1. Fetch test metadata
        const testRes = await axios.get(`/api/tests/public/${slug}`);
        const testData = testRes.data.test || testRes.data;
        setTest(testData);
        testRef.current = testData;

        // 2. Check if already submitted
        const checkRes = await axios.get(`/api/submissions/check/${testData._id}`, {
          headers: { Authorization: `Bearer ${candidateToken}` }
        });
        if (checkRes.data.alreadySubmitted) {
          navigate(`/exam/${slug}/complete`, {
            state: { submission: null }
          });
          return;
        }

        const isRandomized = testData.questionsPerCandidate > 0 && !testData.questions;

        if (isRandomized) {
          // 3. Call /submissions/start to get randomized questions
          const startRes = await axios.post('/api/submissions/start', {
            testId: testData._id
          }, {
            headers: { Authorization: `Bearer ${candidateToken}` }
          });

          const { submissionId: subId, questions } = startRes.data;
          setSubmissionId(subId);
          submissionIdRef.current = subId;
          setExamQuestions(questions);
          examQuestionsRef.current = questions;
          setAnswers(new Array(questions.length).fill(''));
          setDontKnow(new Array(questions.length).fill(false));
        } else {
          // Non-randomized: use questions from public endpoint
          const questions = (testData.questions || []).map((q, i) => ({
            originalIndex: i,
            question: q.question,
            points: q.points || 1,
          }));
          setExamQuestions(questions);
          examQuestionsRef.current = questions;
          setAnswers(new Array(questions.length).fill(''));
          setDontKnow(new Array(questions.length).fill(false));
        }

        setTimeLeft(testData.duration * 60);
      } catch (err) {
        if (err.response?.data?.alreadySubmitted) {
          navigate(`/exam/${slug}/complete`, { state: { submission: null } });
          return;
        }
        setError('Failed to load exam. Please go back and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchAndStart();
  }, [slug, navigate, mediaStatus]);

  // Timer
  useEffect(() => {
    if (!test || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          submitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [test, submitExam, timeLeft]);

  // Anti-cheat measures
  useEffect(() => {
    if (!test) return;

    const handlePaste = (e) => {
      e.preventDefault();
      const flag = `paste_attempt_during_Q${currentQuestionRef.current + 1}_at_${new Date().toISOString()}`;
      setCheatFlags(prev => [...prev, flag]);
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        const flag = `tab_switch_during_Q${currentQuestionRef.current + 1}_at_${new Date().toISOString()}`;
        setCheatFlags(prev => [...prev, flag]);
        notify('Warning: Tab switching has been detected and logged. Please stay on the exam page.', 'warning');
      }
    };

    document.addEventListener('paste', handlePaste);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [test]);

  const updateAnswer = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const toggleDontKnow = (index) => {
    const newDontKnow = [...dontKnow];
    newDontKnow[index] = !newDontKnow[index];
    setDontKnow(newDontKnow);
    if (!newDontKnow[index] === false) {
      // unchecked, keep answer
    } else {
      // checked, clear answer
      const newAnswers = [...answers];
      newAnswers[index] = '';
      setAnswers(newAnswers);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (!test) return 'timer';
    const totalSeconds = test.duration * 60;
    const ratio = timeLeft / totalSeconds;
    if (ratio <= 0.1) return 'timer timer-red';
    if (ratio <= 0.25) return 'timer timer-yellow';
    return 'timer timer-green';
  };

  const totalQuestions = examQuestions.length;
  const answeredCount = answers.filter((a, i) => a.trim() !== '' || dontKnow[i]).length;
  // Submit unlocks only once the LAST question has input (typed or "Don't Know").
  // Stays unlocked afterward so candidates can still go Back to review.
  const lastIndex = totalQuestions - 1;
  const canSubmit = lastIndex >= 0 && !!((answers[lastIndex] || '').trim() || dontKnow[lastIndex]);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  if (isMobile) {
    return (
      <div className="exam-page exam-taking">
        <div className="exam-container">
          <div className="exam-card error-card">
            <h2>Desktop Required</h2>
            <p>This exam can only be taken on a desktop or laptop computer.</p>
            <p>Please open this link on a desktop browser to proceed.</p>
          </div>
        </div>
      </div>
    );
  }

  if (mediaStatus === 'idle' || mediaStatus === 'requesting') {
    return (
      <div className="exam-page exam-taking">
        <div className="exam-container">
          <div className="exam-card">
            <h2>Proctored exam</h2>
            <p>This exam is monitored. Please allow camera and microphone access when prompted to begin.</p>
            <p style={{ color: '#64748b', fontSize: 14 }}>Your camera and microphone are monitored and your session is recorded and submitted along with your answers.</p>
            <div className="loading-spinner">Waiting for camera &amp; microphone permission...</div>
          </div>
        </div>
      </div>
    );
  }

  if (mediaStatus === 'denied') {
    return (
      <div className="exam-page exam-taking">
        <div className="exam-container">
          <div className="exam-card error-card">
            <h2>Camera &amp; microphone required</h2>
            <p>{mediaError}</p>
            <p style={{ color: '#64748b', fontSize: 14 }}>This exam is proctored — your session is monitored and recorded. You cannot start the exam without granting camera and microphone access.</p>
            <button className="btn btn-primary" onClick={requestMedia}>Grant access &amp; retry</button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="exam-page exam-taking">
        <div className="exam-container">
          <div className="loading-spinner">Loading exam...</div>
        </div>
      </div>
    );
  }

  if (error && !test) {
    return (
      <div className="exam-page exam-taking">
        <div className="exam-container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!test || totalQuestions === 0) return null;

  const currentQ = examQuestions[currentQuestion];

  return (
    <div className="exam-page exam-taking">
      {/* Proctoring self-view (deterrent; reuses cheatFlags pipeline) */}
      <div style={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000, width: 180, background: '#000', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.4)', border: '2px solid #e11d48' }}>
        <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: 135, objectFit: 'cover', display: 'block', background: '#000' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#e11d48', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
          REC &middot; Monitored
        </div>
      </div>

      {cameraLost && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', padding: '28px 32px', borderRadius: 10, maxWidth: 420, textAlign: 'center' }}>
            <h2 style={{ marginTop: 0, color: '#e11d48' }}>Monitoring interrupted</h2>
            <p>Your camera or microphone was turned off. This has been recorded and flagged. You must re-enable it to continue — the timer is still running.</p>
            <button className="btn btn-primary" onClick={requestMedia}>Re-enable camera &amp; mic</button>
          </div>
        </div>
      )}

      {/* Top bar with timer */}
      <div className="exam-topbar">
        <div className="exam-topbar-left">
          <h2>{test.title}</h2>
        </div>
        <div className="exam-topbar-center">
          <div className={getTimerClass()}>
            <span className="timer-icon">&#9200;</span>
            <span className="timer-text">{formatTime(timeLeft)}</span>
          </div>
        </div>
        <div className="exam-topbar-right">
          <span className="progress-text">{answeredCount}/{totalQuestions} answered</span>
          <button
            className="btn btn-primary btn-submit-exam"
            onClick={() => confirm('Are you sure you want to submit the exam?', submitExam)}
            disabled={submitting || !canSubmit}
            title={!canSubmit ? 'Submit unlocks on the last question, once you have answered it' : undefined}
          >
            {submitting ? 'Submitting...' : 'Submit Exam'}
          </button>
        </div>
      </div>

      <div className="exam-body">
        {/* Question navigation sidebar */}
        <div className="question-nav">
          <h3>Questions</h3>
          <div className="question-nav-grid">
            {examQuestions.map((_, i) => (
              <div
                key={i}
                className={`question-nav-btn ${i === currentQuestion ? 'active' : ''} ${
                  answers[i]?.trim() ? 'answered' : ''
                } ${dontKnow[i] ? 'skipped' : ''}`}
                style={{ cursor: 'default' }}
                aria-current={i === currentQuestion ? 'true' : undefined}
              >
                {i + 1}
              </div>
            ))}
          </div>
          <div className="question-nav-legend">
            <div className="legend-item">
              <span className="legend-dot answered"></span> Answered
            </div>
            <div className="legend-item">
              <span className="legend-dot skipped"></span> Don't Know
            </div>
            <div className="legend-item">
              <span className="legend-dot unanswered"></span> Unanswered
            </div>
          </div>
        </div>

        {/* Main question area */}
        <div className="question-area">
          {error && <div className="error-message">{error}</div>}

          <div className="question-card">
            <div className="question-card-header">
              <span className="question-number-badge">Question {currentQuestion + 1} of {totalQuestions}</span>
              <span className="points-badge">{currentQ?.points || 1} point{(currentQ?.points || 1) > 1 ? 's' : ''}</span>
            </div>
            <div className="question-card-body">
              <p className="question-text-exam">{currentQ?.question}</p>
              <div className="answer-section">
                <label htmlFor="answer" className="answer-label">Your Answer</label>
                <textarea
                  id="answer"
                  value={answers[currentQuestion] || ''}
                  onChange={(e) => updateAnswer(currentQuestion, e.target.value)}
                  placeholder={dontKnow[currentQuestion] ? "Marked as 'Don't Know'" : "Type your answer here..."}
                  rows={8}
                  disabled={dontKnow[currentQuestion]}
                  className={dontKnow[currentQuestion] ? 'textarea-disabled' : ''}
                />
                <div className="dont-know-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={dontKnow[currentQuestion]}
                      onChange={() => toggleDontKnow(currentQuestion)}
                    />
                    <span>I don't know the answer to this question</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="question-card-footer">
              <button
                className="btn btn-outline"
                onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                disabled={currentQuestion === 0}
              >
                &larr; Previous
              </button>
              {(answers[currentQuestion]?.trim() || dontKnow[currentQuestion]) && (
                currentQuestion < totalQuestions - 1 ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => setCurrentQuestion(prev => Math.min(totalQuestions - 1, prev + 1))}
                  >
                    Next &rarr;
                  </button>
                ) : (
                  <button
                    className="btn btn-primary btn-submit-exam"
                    onClick={() => confirm('Are you sure you want to submit the exam?', submitExam)}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Exam'}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TakeExam;
